import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'

import { CreateObject } from './create-object'
import { CreateTuple } from './create-tuple'

/**
 * Simply produces the given argument, mostly unchanged. This is primarily
 * useful for creating complex objects and arrays that do not require dynamic
 * values/expressions
 *
 * In some cases, you may want a complex object that only requires one or two
 * dynamic values. In those cases, you can use `{ "``": ... }` to render an
 * expression where the `...` is. See below for a full example
 *
 * @param value The value to be returned
 * @example
 * // Simple injection
 * {
 *   '`': {
 *     a: {
 *       b: {
 *         c: [ 1, 2, 3 ]
 *       }
 *     }
 *   }
 * }
 * // ->
 * {
 *   a: {
 *     b: {
 *       c: [ 1, 2, 3 ]
 *     }
 *   }
 * }
 *
 * // Escaped expression
 * {
 *   '`': {
 *     a: {
 *       b: {
 *         c: [ 1, 2, { '``': { '+': [ 1, 2 ] } } ]
 *       }
 *     }
 *   }
 * }
 * // ->
 * {
 *   a: {
 *     b: {
 *       c: [ 1, 2, 3 ]
 *     }
 *   }
 * }
 */
export interface Inject<out T = any> {
  /** {@link Inject} */
  readonly [Key.Inject]: unknown
}
export namespace Inject {
  export const isInject: TypeChecker<Inject> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Inject>({
      [Key.Inject]: TypeChecker.isUnknown,
    }),
  ).usingAlias('Fn.Inject')
  export const assertIsInject: TypeAsserter<Inject> =
    TypeAsserter.Factory.from(isInject)

  export const ESCAPE_KEY = '``'

  export const HELPER: FnHelper<Inject> = {
    createNode: (fn, context) => {
      interface Iteration {
        value: unknown
        node?: expressionNode.Node
        children?: Iteration[] | Record<string, Iteration>
      }
      const root: Iteration = {
        value: fn[Key.Inject],
      }
      if (!TypeChecker.isObject(root.value)) {
        // If the value is not an object, we just return it as is
        return new expressionNode.ForValue(root.value)
      }
      const iterations: Iteration[] = [root]
      let curr: Iteration | undefined
      while ((curr = iterations.pop())) {
        if (curr.children) {
          // If this iteration already has children, then we know we are
          // returning to this iteration after the children have been evaluated,
          // so all that is left is to put the result together
          let hasAllValueNodes = true
          if (TypeChecker.isReadonlyArray(curr.children)) {
            // If we are creating an array, we need to collect all of the child
            // expression nodes in an array
            const elements: expressionNode.Node[] = []
            for (const child of curr.children) {
              if (!child.node) {
                // TODO: clean up
                throw new Error('childNode is undefined')
              }
              hasAllValueNodes &&= child.node instanceof expressionNode.ForValue
              elements.push(child.node)
            }
            if (hasAllValueNodes) {
              // If all the child expression nodes are value nodes, then we can
              // just create a value node for the array
              curr.node = new expressionNode.ForValue(
                // NOTE: we've already checked that all element nodes are value
                // nodes
                elements.map(
                  element => (element as expressionNode.ForValue).value,
                ),
              )
            } else {
              // If any of the child expression nodes are not value nodes, we
              // need to use a CreateTuple node
              curr.node = new CreateTuple.Node(elements)
            }
          } else {
            // If we are creating an object, we need to collect all of the child
            // expression nodes in an object
            const fields: Record<string, expressionNode.Node> = {}
            for (const key in curr.children) {
              const childNode = curr.children[key].node
              if (!childNode) {
                // TODO: clean up
                throw new Error('childNode is undefined')
              }
              hasAllValueNodes &&= childNode instanceof expressionNode.ForValue
              fields[key] = childNode
            }
            if (hasAllValueNodes) {
              // If all the child expression nodes are value nodes, then we can
              // just create a value node for the object
              const value: Record<string, unknown> = {}
              for (const key in fields) {
                // NOTE: we've already checked that all field nodes are value
                // nodes
                value[key] =
                  (fields[key] as expressionNode.ForValue).value ?? null
              }
              curr.node = new expressionNode.ForValue(value)
            } else {
              // If any of the child expression nodes are not value nodes, we
              // need to use a CreateObject node
              curr.node = new CreateObject.Node(fields)
            }
          }
        } else {
          // If the current iteration does not already have children, we need to
          // process the object to find any children that need to be processed,
          // or find an escaped expression if there is one
          const escapedExpression = (curr.value as Unknown.ButNotNullish)[
            ESCAPE_KEY
          ]
          if (escapedExpression !== undefined) {
            // If this current iteration contains an escaped expression, we just
            // evaluate that expression
            curr.node = expressionNode.from(escapedExpression, context)
          } else if (TypeChecker.isReadonlyArray(curr.value)) {
            // If the current iteration is an array, the children need to be
            // structured in an array
            curr.children = curr.value.map(childValue => ({
              // If this child value is an object, then we will need to process
              // the child iteration to get a node. Otherwise, we can just put
              // the child value in a value node
              node: TypeChecker.isObject(childValue)
                ? undefined
                : new expressionNode.ForValue(childValue),
              value: childValue,
            }))
            iterations.push(
              // Once we've processed all the children, we will need to come
              // back and process this array (note: we process the iterations
              // list in reverse order)
              curr,
              // We only need to process children that don't already have nodes
              // (i.e. objects/arrays)
              ...curr.children.filter(child => !child.node),
            )
          } else {
            // If the current iteration is an object (not array), the children
            // need to be structured in an object
            const children: Record<string, Iteration> = (curr.children = {})
            const value = curr.value as Unknown.ButNotNullish
            const childrenThatNeedProcessing: Iteration[] = []
            for (const key in value) {
              const childValue = value[key]
              const child = (children[key] = {
                node: TypeChecker.isObject(childValue)
                  ? undefined
                  : new expressionNode.ForValue(childValue),
                value: childValue,
              })
              if (!child.node) {
                // We only need to process children that don't already have nodes
                // (i.e. objects/arrays)
                childrenThatNeedProcessing.push(child)
              }
            }
            iterations.push(
              // Once we've processed all the children, we will need to come
              // back and process this object (note: we process the iterations
              // list in reverse order)
              curr,
              ...childrenThatNeedProcessing,
            )
          }
        }
      }

      // Finally, we return the generated node for the root value, assuming it
      // was successfully created
      TypeAsserter.assertIsNotNullish(
        root.node,
        'Failed to properly process the Inject function',
      )
      return root.node
    },
    expressionsInArgs: fn => {
      const rootValue = fn[Key.Inject]
      if (!TypeChecker.isObject(rootValue)) {
        // If the value is not an object, it cannot contain any expressions
        return []
      }
      const expressions: Expression[] = []
      const objectsThatMightContainEscapedExpressions: Unknown.ButNotNullish[] =
        [rootValue as Unknown.ButNotNullish]
      let curr: Unknown.ButNotNullish | undefined
      while ((curr = objectsThatMightContainEscapedExpressions.pop())) {
        const escapedExpression = curr[Inject.ESCAPE_KEY]
        if (escapedExpression) {
          expressions.push(escapedExpression)
        } else {
          objectsThatMightContainEscapedExpressions.push(
            // NOTE: since we are checking if they are objects, we know they are
            // not nullish
            ...(Object.values(curr).filter(
              TypeChecker.isObject,
            ) as Unknown.ButNotNullish[]),
          )
        }
      }
      return expressions
    },
    typeChecker: isInject,
  }
}
